import React, { useCallback, useEffect, useState } from 'react';

import {
  DataGrid,
  Column,
  Lookup,
  HeaderFilter,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { FiEdit2, FiPlus } from 'react-icons/fi';
import { IconButton, Tooltip } from '@material-ui/core';
import { WhisperSpinner } from 'react-spinners-kit';
import { Container, Body, ContainerLoading } from './styles';
import { HeaderComponent } from '../../../components/Header';
import { useDocumentTitle } from '../../../hooks/documentTitle';
import api from '../../../services/api';
import { Button } from '../../../components/Button';
import { DialogAddCategory } from './DialogAddCategory';
import { DialogEditCategory } from './DialogEditCategory';

export const Categories: React.FC<{ title?: string }> = ({ title }) => {
  const { setTitle } = useDocumentTitle();
  const [firstLoading, setFirstLoading] = useState(true);
  const [categories, setCategories] = useState<DataSource>(new DataSource([]));
  const [contractTypes, setContractTypes] = useState<CustomStore>();
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editKey, setEditKey] = useState(0);

  const loadCategories = useCallback(async () => {
    const categoriesStore = new CustomStore({
      key: 'id',
      loadMode: 'raw',
      load: async () => {
        const { data } = await api.get('/api/type-categories');
        setFirstLoading(false);
        return data;
      },
      insert: async data => {
        await api.post('/api/type-categories', data);
      },
      update: async (id, data) => {
        await api.put(`/api/type-categories/${id}`, data);
      },
    });

    setCategories(
      new DataSource({
        store: categoriesStore,
        paginate: true,
        reshapeOnPush: true,
      }),
    );
  }, []);

  const loadContractTypes = useCallback(async () => {
    setContractTypes(
      new CustomStore({
        key: 'id',
        loadMode: 'raw',
        load: async () => {
          const { data } = await api.get('/api/contract-types');
          return data;
        },
      }),
    );
  }, []);

  useEffect(() => {
    setTitle(title);
    loadContractTypes();
    loadCategories();
  }, [setTitle, title, loadCategories, loadContractTypes]);

  const editCell = useCallback(e => {
    return (
      <Tooltip title="Edit Category" aria-label="open">
        <IconButton
          aria-label="Edit Category"
          size="small"
          onClick={() => {
            setEditKey(e.key);
            setOpenEdit(true);
          }}
        >
          <FiEdit2 size={20} color="#8b0304" />
        </IconButton>
      </Tooltip>
    );
  }, []);

  return (
    <Container>
      <HeaderComponent />
      {firstLoading && (
        <ContainerLoading>
          <WhisperSpinner size={50} backColor="#8b0304" />
        </ContainerLoading>
      )}
      <Body>
        <div className="gridContainer">
          <Button
            primary
            className="newCategoryButton"
            onClick={() => setOpenAdd(true)}
          >
            <FiPlus size={20} />
            NEW CATEGORY
          </Button>
          <DataGrid className="grid" dataSource={categories}>
            <HeaderFilter visible allowSearch />
            <Column dataField="idContractType" caption="Document Type">
              <Lookup
                displayExpr="description"
                valueExpr="id"
                dataSource={contractTypes}
              />
            </Column>
            <Column dataField="description" />
            <Column dataField="isActive" width={120} />
            <Column width={80} cellRender={editCell} />
          </DataGrid>
        </div>
      </Body>

      <DialogAddCategory
        open={openAdd}
        handleClose={() => setOpenAdd(false)}
        categories={categories}
      />

      <DialogEditCategory
        open={openEdit}
        categoryId={editKey}
        handleClose={() => setOpenEdit(false)}
        categories={categories}
      />
    </Container>
  );
};
